import { useState } from 'react';
import { motion } from 'framer-motion';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

const galleryImages = [
  {
    id: 1,
    src: "https://images.pexels.com/photos/6419128/pexels-photo-6419128.jpeg?auto=compress&cs=tinysrgb&w=800",
    title: "Pipe Installation",
    category: "Residential"
  },
  {
    id: 2,
    src: "https://images.pexels.com/photos/29248902/pexels-photo-29248902.jpeg?auto=compress&cs=tinysrgb&w=800",
    title: "Commercial Plumbing",
    category: "Commercial"
  },
  {
    id: 3,
    src: "https://images.pexels.com/photos/7578999/pexels-photo-7578999.jpeg?auto=compress&cs=tinysrgb&w=800",
    title: "Faucet Repair",
    category: "Plumbing"
  },
  {
    id: 4,
    src: "https://images.pexels.com/photos/7859953/pexels-photo-7859953.jpeg?auto=compress&cs=tinysrgb&w=800",
    title: "Boiler Maintenance",
    category: "Heating"
  },
  {
    id: 5,
    src: "https://images.pexels.com/photos/6653889/pexels-photo-6653889.jpeg?auto=compress&cs=tinysrgb&w=800",
    title: "Bathroom Renovation",
    category: "Residential"
  },
  {
    id: 6,
    src: "https://images.pexels.com/photos/7937300/pexels-photo-7937300.jpeg?auto=compress&cs=tinysrgb&w=800",
    title: "Industrial Pipes",
    category: "Commercial"
  },
];

const categories = ['All', 'Residential', 'Commercial', 'Heating', 'Plumbing'];

const ProjectGallery = () => {
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImage, setCurrentImage] = useState(0);

  const filteredImages = selectedCategory === 'All' 
    ? galleryImages 
    : galleryImages.filter(img => img.category === selectedCategory);

  const openLightbox = (index) => {
    setCurrentImage(index);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
  };

  const nextImage = () => {
    setCurrentImage((prev) => (prev + 1) % filteredImages.length);
  };

  const prevImage = () => {
    setCurrentImage((prev) => (prev - 1 + filteredImages.length) % filteredImages.length);
  };

  return (
    <div data-testid="project-gallery-page">
      {/* Hero Section */}
      <section className="py-16 bg-[#14366C]" data-testid="gallery-hero">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="font-oswald uppercase text-4xl md:text-5xl font-bold text-white mb-4">
              Project Gallery
            </h1>
            <p className="text-gray-300 text-lg max-w-2xl mx-auto">
              Browse through our completed projects and see the quality of our work in residential and commercial plumbing, heating, and remodeling.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Filter Buttons */}
      <section className="py-8 bg-[#F8F8F8]">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex flex-wrap justify-center gap-3">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-6 py-2 font-oswald uppercase tracking-wide text-sm transition-colors ${
                  selectedCategory === category
                    ? 'bg-[#F58220] text-white'
                    : 'bg-white text-[#14366C] hover:bg-[#14366C] hover:text-white'
                }`}
                data-testid={`filter-${category.toLowerCase()}`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="py-16 bg-white" data-testid="gallery-grid">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredImages.map((image, index) => (
              <motion.div
                key={image.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="group cursor-pointer"
                onClick={() => openLightbox(index)}
              >
                <div className="relative overflow-hidden shadow-md">
                  <img 
                    src={image.src} 
                    alt={image.title}
                    className="w-full aspect-[4/3] object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-[#14366C]/0 group-hover:bg-[#14366C]/70 transition-colors flex items-center justify-center">
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity text-center text-white">
                      <h3 className="font-oswald text-xl font-bold">{image.title}</h3>
                      <p className="text-sm text-gray-200">{image.category}</p>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Lightbox */}
      {lightboxOpen && (
        <div 
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center"
          onClick={closeLightbox}
        >
          <button 
            className="absolute top-4 right-4 text-white hover:text-[#F58220] transition-colors"
            onClick={closeLightbox}
            data-testid="lightbox-close"
          >
            <X className="w-10 h-10" />
          </button>
          
          <button 
            className="absolute left-4 top-1/2 -translate-y-1/2 text-white hover:text-[#F58220] transition-colors"
            onClick={(e) => { e.stopPropagation(); prevImage(); }}
            data-testid="lightbox-prev"
          >
            <ChevronLeft className="w-12 h-12" />
          </button>
          
          <div className="max-w-4xl max-h-[80vh] px-16" onClick={(e) => e.stopPropagation()}>
            <img 
              src={filteredImages[currentImage]?.src} 
              alt={filteredImages[currentImage]?.title}
              className="max-w-full max-h-[80vh] object-contain"
            />
            <div className="text-center mt-4">
              <h3 className="font-oswald text-xl text-white font-bold">{filteredImages[currentImage]?.title}</h3>
              <p className="text-gray-400">{filteredImages[currentImage]?.category}</p>
            </div>
          </div>
          
          <button 
            className="absolute right-4 top-1/2 -translate-y-1/2 text-white hover:text-[#F58220] transition-colors"
            onClick={(e) => { e.stopPropagation(); nextImage(); }}
            data-testid="lightbox-next"
          >
            <ChevronRight className="w-12 h-12" />
          </button>
        </div>
      )}

      {/* CTA Section */}
      <section className="py-12 bg-[#14366C]">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <h2 className="font-oswald uppercase text-2xl font-bold text-white mb-4">
            Ready to Start Your Project?
          </h2>
          <p className="text-gray-300 mb-6">
            Contact us today for your plumbing or heating project.
          </p>
          <a 
            href="tel:651-738-9354" 
            className="inline-block bg-[#F58220] hover:bg-[#e07010] text-white font-oswald font-bold uppercase tracking-wider px-8 py-3 transition-colors"
          >
            Call (651) 738-9354
          </a>
        </div>
      </section>
    </div>
  );
};

export default ProjectGallery;
